import { Form } from '@iheartjane/reefer-hook-form';
import { useEffect, useRef, useState } from 'react';
import type { ControllerRenderProps, FieldValues, Path } from 'react-hook-form';

import { isValidUrl } from './UrlInput';

import styles from './UrlInput.module.css';

interface UrlEmailPhoneInputProps<TFieldValues extends FieldValues> {
  field: ControllerRenderProps<TFieldValues, Path<TFieldValues>>;
  placeholder?: string;
}

const isEmail = (value: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(value);
};

const isPhoneNumber = (value: string): boolean => {
  const phoneRegex = /^[\d\s\-()+]+$/;
  const digitsOnly = value.replace(/\D/g, '');
  return phoneRegex.test(value) && digitsOnly.length >= 10;
};

const normalizeValue = (value: string): string => {
  const trimmed = value.replaceAll(' ', '');

  if (!trimmed) {
    return '';
  }

  if (
    trimmed.startsWith('https://') ||
    trimmed.startsWith('http://') ||
    trimmed.startsWith('mailto:') ||
    trimmed.startsWith('tel:')
  ) {
    return trimmed;
  }

  if (isEmail(trimmed)) {
    return `mailto:${trimmed}`;
  }

  if (isPhoneNumber(trimmed)) {
    return `tel:${trimmed}`;
  }

  if (isValidUrl(trimmed)) {
    return `https://${trimmed}`;
  }

  return trimmed;
};

const validateInput = (value: string): string | true => {
  if (!value) {
    return true;
  }

  if (value.startsWith('mailto:')) {
    const email = value.slice(7);
    return isEmail(email) ? true : 'Invalid email address';
  }

  if (value.startsWith('tel:')) {
    const phone = value.slice(4);
    return isPhoneNumber(phone) ? true : 'Invalid phone number';
  }

  if (value.startsWith('https://') || value.startsWith('http://')) {
    return isValidUrl(value)
      ? true
      : 'Invalid URL - must include a domain (e.g., .com, .org)';
  }

  return 'Please enter a valid URL, email, or phone number';
};

const DEBOUNCE_MS = 800;

export const UrlEmailPhoneInput = <TFieldValues extends FieldValues>({
  field,
  placeholder = 'Add URL, email, or phone',
}: UrlEmailPhoneInputProps<TFieldValues>) => {
  const [displayValue, setDisplayValue] = useState<string>(
    field.value === null || field.value === undefined ? '' : field.value
  );
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isNormalizingRef = useRef(false);

  useEffect(() => {
    if (!isNormalizingRef.current) {
      const safeValue =
        field.value === null || field.value === undefined ? '' : field.value;
      setDisplayValue(safeValue);
    }
  }, [field.value]);

  const handleChange = (newValue: string) => {
    setDisplayValue(newValue);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      isNormalizingRef.current = true;

      const normalized = normalizeValue(newValue);

      setDisplayValue(normalized);
      field.onChange(normalized);

      setTimeout(() => {
        isNormalizingRef.current = false;
      }, 0);
    }, DEBOUNCE_MS);
  };

  const handleBlur = () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    isNormalizingRef.current = true;

    const normalized = normalizeValue(displayValue);

    setDisplayValue(normalized);
    field.onChange(normalized);
    field.onBlur();

    setTimeout(() => {
      isNormalizingRef.current = false;
    }, 0);
  };

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return (
    <Form.TextField
      className={styles['urlField']}
      name={field.name}
      ref={field.ref}
      label=""
      labelHidden
      value={displayValue}
      placeholder={placeholder}
      width="100%"
      onChange={handleChange}
      onBlur={handleBlur}
      validate={validateInput}
    />
  );
};
